import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Field } from '@cloudsign/shared';
import type { FieldScope, FieldTypeInfo, Group } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Button, CheckboxField, ConfirmButton, TextField } from '../../components/ui';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { fieldsPath } from './api';
import type { FieldTarget } from './api';
import { ConfigForm, cleanConfig, defaultConfig } from './ConfigForm';
import { Dialog } from './Dialog';
import { FieldPreview } from './FieldPreview';

export function typeLabel(info: Pick<FieldTypeInfo, 'key' | 'labelKey'>): string {
  const own = `fnUi.type.${info.key}.name`;
  const text = t(own);
  return text !== own ? text : t(info.labelKey);
}

function typeDesc(key: string): string {
  const k = `fnUi.type.${key}.desc`;
  const text = t(k);
  return text !== k ? text : '';
}

/** Add a question (type picker, then the form) or edit one. */
export function FieldDialog({
  target,
  scope,
  types,
  field,
  siteGroups,
  onClose,
}: {
  target: FieldTarget;
  scope: FieldScope;
  types: FieldTypeInfo[];
  field?: Field;
  /** Groups visible at the site (sign-in / sign-out questions only). */
  siteGroups: Group[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const canSensitive = useCan('groups.manage_sensitive');
  const editing = Boolean(field);
  const available = types.filter((x) => x.scopes.includes(scope));
  const [typeKey, setTypeKey] = useState<string | null>(field?.typeKey ?? null);
  const info = types.find((x) => x.key === typeKey) ?? null;
  const [label, setLabel] = useState(field?.label ?? '');
  const [helpText, setHelpText] = useState(field?.helpText ?? '');
  const [required, setRequired] = useState(field?.required ?? false);
  const [sensitive, setSensitive] = useState(field?.sensitive ?? false);
  const [groupIds, setGroupIds] = useState<string[]>(field?.groupIds ?? siteGroups.map((g) => g.id));
  const [config, setConfig] = useState<Record<string, unknown>>(field?.config ?? {});
  const [localError, setLocalError] = useState<string | null>(null);
  const isSite = target.kind === 'site';

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['fields', target.kind, target.id] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => {
      const cleaned = cleanConfig(config);
      const common = {
        label: label.trim(),
        helpText: helpText.trim(),
        required,
        sensitive: info?.canBeSensitive ? sensitive : false,
        config: cleaned,
        ...(isSite ? { groupIds } : {}),
      };
      if (field) return api.patch(`${fieldsPath(target)}/${field.id}`, { body: common, schema: Field });
      return api.post(fieldsPath(target), {
        body: { ...common, scope, ...(isSite ? { siteId: target.id } : { groupId: target.id }), typeKey },
        schema: Field,
      });
    },
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: () => api.patch(`${fieldsPath(target)}/${field!.id}`, { body: { status: 'archived' }, schema: Field }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`${fieldsPath(target)}/${field!.id}`), onSuccess: refresh });

  const pick = (x: FieldTypeInfo) => {
    setTypeKey(x.key);
    setConfig(defaultConfig(x.configJsonSchema));
    if (!x.canBeSensitive) setSensitive(false);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (Array.isArray(config['options']) && (cleanConfig(config)['options'] as unknown[]).length === 0) {
      setLocalError(t('fnUi.dialog.optionsNeeded'));
      return;
    }
    save.mutate();
  };

  const errs = fieldErrors(save.error);
  const title = field ? t('fnUi.dialog.editTitle', { name: field.label }) : target.kind === 'group' ? t('fnUi.dialog.addPersonalTitle') : t('fnUi.dialog.addTitle');
  const busy = save.isPending || archive.isPending || remove.isPending;

  return (
    <Dialog title={title} onClose={onClose} wide>
      {!editing && !info ? (
        <fieldset>
          <legend className="mb-3 text-sm font-medium text-slate-800">{t('fnUi.dialog.pickType')}</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {available.map((x) => (
              <button
                key={x.key}
                type="button"
                onClick={() => pick(x)}
                className="rounded-lg border border-slate-300 bg-white p-3 text-left hover:border-brand hover:bg-slate-50"
              >
                <span className="block text-sm font-semibold text-slate-900">{typeLabel(x)}</span>
                <span className="block text-xs text-slate-600">{typeDesc(x.key)}</span>
              </button>
            ))}
          </div>
        </fieldset>
      ) : (
        <form onSubmit={submit} noValidate className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            {info ? <p className="text-sm text-slate-600">{t('fnUi.dialog.typeLocked', { type: typeLabel(info) })}</p> : null}
            <TextField
              label={target.kind === 'group' ? t('fnUi.dialog.labelPersonal') : t('fnUi.dialog.label')}
              value={label}
              maxLength={120}
              required
              error={errs['label']}
              onChange={(e) => setLabel(e.target.value)}
            />
            <TextField label={t('fnUi.dialog.helpText')} hint={t('fnUi.dialog.helpHint')} value={helpText} maxLength={300} onChange={(e) => setHelpText(e.target.value)} />
            <CheckboxField label={t('fnUi.dialog.required')} checked={required} onChange={(e) => setRequired(e.target.checked)} />
            {info?.canBeSensitive && canSensitive ? (
              <div className="space-y-2">
                <CheckboxField label={t('fnUi.dialog.sensitive')} hint={t('fnUi.dialog.sensitiveHint')} checked={sensitive} onChange={(e) => setSensitive(e.target.checked)} />
                {sensitive ? <Alert kind="info">{t('fnUi.dialog.sensitiveWarning')}</Alert> : null}
              </div>
            ) : null}
            {isSite ? (
              <fieldset>
                <legend className="text-sm font-medium text-slate-800">{t('fnUi.dialog.groups')}</legend>
                <p className="mb-2 text-xs text-slate-600">{t('fnUi.dialog.groupsHint')}</p>
                {siteGroups.length === 0 ? <p className="text-sm text-slate-600">{t('fnUi.dialog.noGroups')}</p> : null}
                <div className="space-y-2">
                  {siteGroups.map((g) => (
                    <CheckboxField
                      key={g.id}
                      label={g.name}
                      checked={groupIds.includes(g.id)}
                      onChange={(e) => setGroupIds(e.target.checked ? [...groupIds, g.id] : groupIds.filter((x) => x !== g.id))}
                    />
                  ))}
                </div>
              </fieldset>
            ) : null}
            {info && Object.keys((info.configJsonSchema['properties'] ?? {}) as object).length ? (
              <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
                <legend className="px-1 text-sm font-medium text-slate-800">{t('fnUi.dialog.settings')}</legend>
                <ConfigForm schema={info.configJsonSchema} config={config} onChange={setConfig} />
              </fieldset>
            ) : null}
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-medium text-slate-800">{t('fnUi.dialog.preview')}</h3>
              <FieldPreview widget={info?.widget ?? null} label={label} helpText={helpText} required={required} config={config} />
            </div>
          </div>
          <div className="space-y-3 lg:col-span-2">
            {localError ? <Alert>{localError}</Alert> : null}
            <ErrorLine error={save.error ?? archive.error ?? remove.error} />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {field ? (
                  <>
                    <Button disabled={busy} onClick={() => archive.mutate()}>
                      {t('fnUi.dialog.archive')}
                    </Button>
                    <ConfirmButton label={t('fnUi.dialog.delete')} confirmLabel={t('fnUi.dialog.deleteConfirm')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
                  </>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button type="submit" variant="primary" disabled={busy || !label.trim()}>
                  {field ? t('fnUi.dialog.save') : t('fnUi.dialog.create')}
                </Button>
              </div>
            </div>
            {field ? <p className="text-xs text-slate-600">{t('fnUi.dialog.archiveHint')}</p> : null}
          </div>
        </form>
      )}
    </Dialog>
  );
}
